import React, { useState } from "react";
import {
    Box,
    Grid,
    TextField,
    Paper,
    MenuItem,
    Typography,
    FormControlLabel,
    Switch,
    Button,
    Checkbox,
    IconButton,
    Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { green } from "@mui/material/colors";
import Header from "../../components/Header";
import SearchableDataGrid from '../../components/SearchableDataGrid';

const Roles = () => {
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        startDate: "",
        endDate: "",
        active: true,
        type: "Custom",
        personaEnabled: false,
        report: "",
        dataAccess: ""
    });

    const [rows, setRows] = useState([]);
    const [selectionModel, setSelectionModel] = useState([]);

    /* ================= FORM HANDLER ================= */

    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value
        }));
    };

    /* ================= ADD ROW ================= */

    const addRow = () => {
        setRows((prev) => [
            ...prev,
            {
                id: prev.length + 1,
                functionId: "",
                module: "",
                typeFG: "",
                description: "",
                startDate: "",
                endDate: "",
                active: true
            }
        ]);
    };

    /* ================= DELETE ROW ================= */

    const deleteRow = () => {
        const updated = rows
            .filter((row) => !selectionModel.includes(row.id))
            .map((row, index) => ({
                ...row,
                id: index + 1
            }));

        setRows(updated);
        setSelectionModel([]);
    };

    const processRowUpdate = (newRow) => {
        const updatedRows = rows.map((row) =>
            row.id === newRow.id ? newRow : row
        );
        setRows(updatedRows);
        return newRow;
    };


    /* ================= COLUMNS ================= */

    const columns = [
        { field: "id", headerName: "S No", width: 70 },
        {
            field: "functionId",
            headerName: "Functions ID/Group ID",
            flex: 1,
            editable: true
        },
        {
            field: "module",
            headerName: "Module",
            width: 150,
            editable: true
        },
        {
            field: "typeFG",
            headerName: "Type F/G",
            width: 150,
            editable: true
        },
        {
            field: "description",
            headerName: "Description",
            flex: 1,
            editable: true
        },
        {
            field: "startDate",
            headerName: "Start Date",
            width: 150,
            editable: true,
            type: "date"
        },
        {
            field: "endDate",
            headerName: "End Date",
            width: 150,
            editable: true,
            type: "date"
        },
        {
            field: "active",
            headerName: "Active",
            width: 110,
            renderCell: (params) => (
                <Switch
                    checked={params.value || false}
                    onChange={(e) => {
                        const updatedRows = rows.map((row) =>
                            row.id === params.id
                                ? { ...row, active: e.target.checked }
                                : row
                        );
                        setRows(updatedRows);
                    }}
                    sx={{
                        "& .Mui-checked": { color: green[600] },
                        "& .Mui-checked + .MuiSwitch-track": {
                            backgroundColor: green[600]
                        }
                    }}
                />
            )
        }
    ];

    const handleSave = () => {
        const payload = {
            master: formData,
            details: rows
        };

        console.log("Saving Data:", payload);

        // 👉 Call your API here
        // axios.post("/api/roles", payload)
    };

    const handleReset = () => {
        setFormData({
            name: "",
            description: "",
            startDate: "",
            endDate: "",
            active: true,
            type: "Custom",
            personaEnabled: false,
            report: "",
            dataAccess: ""
        });

        setRows([]);
        setSelectionModel([]);
    };

    return (
        <Box m="20px">
            <Header title="Create Roles" />

            {/* ================= MASTER FORM ================= */}

            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mt: 2 }}>
                <Grid container spacing={2}>

                    {/* ================= ROW 1 ================= */}
                    <Grid item xs={3}>
                        <TextField
                            label="Name"
                            name="name"
                            fullWidth
                            value={formData.name}
                            onChange={handleFormChange}
                        />
                    </Grid>

                    <Grid item xs={3}>
                        <TextField
                            label="Description"
                            name="description"
                            fullWidth
                            value={formData.description}
                            onChange={handleFormChange}
                        />
                    </Grid>

                    <Grid item xs={3}>
                        <TextField
                            label="Start Date"
                            name="startDate"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            value={formData.startDate}
                            onChange={handleFormChange}
                        />
                    </Grid>

                    <Grid item xs={3}>
                        <TextField
                            label="End Date"
                            name="endDate"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            value={formData.endDate}
                            onChange={handleFormChange}
                        />
                    </Grid>




                    {/* ================= ROW 2 ================= */}
                    <Grid item xs={3} >
                        <TextField
                            select
                            size="small"
                            label="Type"
                            name="type"
                            fullWidth
                            value={formData.type}
                            onChange={handleFormChange}
                        >
                            <MenuItem value="Custom">Custom</MenuItem>
                            <MenuItem value="Seeded">Seeded</MenuItem>
                        </TextField>
                    </Grid>



                    <Grid item xs={3}>
                        <TextField
                            select
                            size="small"
                            label="Report"
                            name="report"
                            fullWidth
                            value={formData.report}
                            onChange={handleFormChange}
                        >
                            <MenuItem value="Report1">Report 1</MenuItem>
                            <MenuItem value="Report2">Report 2</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={2}>
                        <TextField
                            select
                            size="small"
                            label="Data Access"
                            name="dataAccess"
                            fullWidth
                            value={formData.dataAccess}
                            onChange={handleFormChange}
                        >
                            <MenuItem value="Full">Full Access</MenuItem>
                            <MenuItem value="Restricted">Restricted</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={2}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.personaEnabled}
                                    name="personaEnabled"
                                    onChange={handleFormChange}
                                    sx={{
                                        "& .MuiSwitch-switchBase.Mui-checked": {
                                            color: "#2e7d32",   // thumb color when ON (green)
                                        },
                                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                            backgroundColor: "#2e7d32",  // track color when ON
                                        },
                                    }}
                                />
                            }
                            label="Persona Enabled"
                        />
                    </Grid>

                    <Grid item xs={1} sx={{ ml: 2, mt: 1 }}>
                        <FormControlLabel
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2   // 👈 increase this value (1, 2, 3...) for more spacing
                            }}
                            control={
                                <Checkbox
                                    checked={formData.active}
                                    name="active"
                                    onChange={handleFormChange}
                                />
                            }
                            label="Status"
                        />
                    </Grid>

                </Grid>
            </Paper>

            {/* ================= ERP TOOLBAR ================= */}


            <Box
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                gap={1.5}
                width="100%"
                sx={{
                    backgroundColor: "#f4f6f8",
                    py: 1,
                    px: 2,
                    borderRadius: 2,
                    border: "1px solid #e0e0e0",
                }}
                mt={2}
                mb={2}
            >
                {/* Add Row */}
                <Tooltip title="Add Row">
                    <IconButton
                        size="small"
                        onClick={addRow}
                        sx={{
                            backgroundColor: "#e8f5e9",
                            color: "#2e7d32",
                            transition: "all 0.2s ease",
                            "&:hover": {
                                backgroundColor: "#c8e6c9",
                                transform: "translateY(-2px)",
                            },
                        }}
                    >
                        <AddIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                {/* Delete Row */}
                <Tooltip title="Delete Row">
                    <IconButton
                        size="small"
                        onClick={deleteRow}
                        sx={{
                            backgroundColor: "#ffebee",
                            color: "#c62828",
                            transition: "all 0.2s ease",
                            "&:hover": {
                                backgroundColor: "#ffcdd2",
                                transform: "translateY(-2px)",
                            },
                        }}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* ================= DATA GRID ================= */}

            <Box
                sx={{
                    backgroundColor: "#f5f7fa",   // same as Adapter
                    padding: 2,
                    borderRadius: 2
                }}
            >
                <SearchableDataGrid
                    rows={rows}
                    columns={columns}
                    pageSize={5}
                    rowsPerPageOptions={[5]}
                    checkboxSelection
                    disableRowSelectionOnClick
                    rowSelectionModel={selectionModel}
                    onRowSelectionModelChange={(newSelection) =>
                        setSelectionModel(newSelection)
                    }
                    processRowUpdate={processRowUpdate}
                    experimentalFeatures={{ newEditingApi: true }}
                    sx={{
                        backgroundColor: "#ffffff",
                        borderRadius: 2
                    }}
                />
            </Box>

            {/* ================= SAVE / RESET SECTION ================= */}

            <Box
                display="flex"
                justifyContent="flex-start"
                gap={2}
                mt={3}
            >
                <Button
                    variant="outlined"
                    onClick={handleSave}
                    sx={{
                        backgroundColor: "#1976d2",
                        textTransform: "none",
                        fontWeight: 500,
                        px: 4,
                        "&:hover": {
                            backgroundColor: "#1565c0"
                        }
                    }}
                >
                    Save
                </Button>

                <Button
                    variant="outlined"
                    onClick={handleReset}
                    sx={{
                        backgroundColor: "#1976d2",
                        textTransform: "none",
                        fontWeight: 500,
                        px: 4,
                        "&:hover": {
                            backgroundColor: "#1565c0"
                        }
                    }}
                >
                    Reset
                </Button>
            </Box>
        </Box>
    );
};

export default Roles;